import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  FileText,
  Printer,
  Download,
  Search,
  Calendar,
  User,
  Loader2,
  Phone,
  MapPin,
  ArrowLeft,
  Share2,
  MessageCircle
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";
import { useReactToPrint } from "react-to-print";

const API = window.location.origin;

export default function AccountStatementPage() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const printRef = useRef();
  
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [storeSettings, setStoreSettings] = useState(null);
  const [statement, setStatement] = useState(null);
  
  const [filters, setFilters] = useState({
    type: searchParams.get("type") || "customer",
    entity_id: searchParams.get("customer_id") || searchParams.get("supplier_id") || "",
    date_from: "",
    date_to: ""
  });

  // ✨ Quick Payment modal state — تسديد سريع من نفس الكشف
  const [showPayModal, setShowPayModal] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payNotes, setPayNotes] = useState("");
  const [paySubmitting, setPaySubmitting] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchSuppliers();
    fetchStoreSettings();
  }, []);

  // ✨ توليد الكشف تلقائياً عند الدخول من فاتورة المبيعات (?customer_id=...)
  const autoEntityId = searchParams.get("customer_id") || searchParams.get("supplier_id");
  useEffect(() => {
    if (autoEntityId && token) {
      // انتظر تحميل قائمة العملاء/الموردين قبل التوليد
      const t = setTimeout(() => {
        generateStatement();
      }, 600);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEntityId, token]);

  const fetchCustomers = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/api/customers`, { headers });
      setCustomers(res.data || []);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/api/suppliers`, { headers });
      setSuppliers(res.data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  const fetchStoreSettings = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/api/store-settings`, { headers });
      setStoreSettings(res.data);
    } catch (error) {
      console.log("No store settings found");
    }
  };

  const generateStatement = async () => {
    if (!filters.entity_id) {
      toast.error(filters.type === "customer" ? "يرجى اختيار العميل" : "يرجى اختيار المورد");
      return;
    }

    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/api/account-statement`, {
        headers,
        params: filters
      });
      setStatement(res.data);
    } catch (error) {
      toast.error("حدث خطأ في جلب كشف الحساب");
    } finally {
      setLoading(false);
    }
  };

  // ✨ تسديد سريع — POST /api/customers/{id}/payment
  const submitQuickPayment = async () => {
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) {
      toast.error("أدخل مبلغ صحيح أكبر من صفر");
      return;
    }
    if (filters.type !== "customer" || !filters.entity_id) {
      toast.error("التسديد السريع متاح للعملاء فقط");
      return;
    }
    setPaySubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(
        `${API}/api/customers/${filters.entity_id}/payment`,
        { amount: amt, notes: payNotes.trim() || "تسديد من كشف الحساب", exchange_rate: null },
        { headers }
      );
      toast.success(`✅ تم تسجيل الدفعة (${res.data?.invoice_number || ""})`);
      setShowPayModal(false);
      setPayAmount("");
      setPayNotes("");
      // إعادة تحميل الكشف ليعكس الدفعة الجديدة
      await generateStatement();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "فشل التسديد";
      toast.error(`فشل التسديد: ${msg}`);
    } finally {
      setPaySubmitting(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `كشف حساب - ${statement?.entity_name || ""}`,
  });

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ar-IQ').format(num || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('ar-IQ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const currentDate = new Date().toLocaleDateString('ar-IQ', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const entityList = filters.type === "customer" ? customers : suppliers;

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <FileText className="w-7 h-7 text-emerald-500" />
          كشف حساب
        </h1>
        <p className="text-slate-400 mt-1">
          إنشاء كشف حساب تفصيلي للعملاء أو الموردين
        </p>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <Label className="text-slate-300">نوع الحساب</Label>
              <Select
                value={filters.type}
                onValueChange={(value) => {
                  setFilters(prev => ({ ...prev, type: value, entity_id: "" }));
                  setStatement(null);
                }}
              >
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">عميل (زبون)</SelectItem>
                  <SelectItem value="supplier">مورد</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-300">
                {filters.type === "customer" ? "اختر العميل" : "اختر المورد"}
              </Label>
              <Select
                value={filters.entity_id}
                onValueChange={(value) => setFilters(prev => ({ ...prev, entity_id: value }))}
              >
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-1">
                  <SelectValue placeholder="اختر..." />
                </SelectTrigger>
                <SelectContent>
                  {entityList.map((entity) => (
                    <SelectItem key={entity.id} value={entity.id}>
                      {entity.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-300">من تاريخ</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                className="bg-slate-900 border-slate-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-300">إلى تاريخ</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                className="bg-slate-900 border-slate-700 text-white mt-1"
              />
            </div>

            <div className="flex items-end">
              <Button
                onClick={generateStatement}
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 ml-2" />
                )}
                عرض كشف الحساب
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statement Result */}
      {statement && (
        <>
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
              <Printer className="w-4 h-4 ml-2" />
              طباعة
            </Button>
            {filters.type === "customer" && filters.entity_id && (
              <Button
                onClick={() => setShowPayModal(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="quick-payment-btn"
              >
                💵 تسديد سريع
              </Button>
            )}
            <Button
              onClick={() => {
                const text = `كشف حساب: ${statement.entity_name}\nالرصيد: ${formatNumber(statement.balance)} د.ع`;
                if (navigator.share) {
                  navigator.share({ title: "كشف حساب", text });
                } else {
                  navigator.clipboard.writeText(text);
                  toast.success("تم نسخ الملخص");
                }
              }}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="share-statement-btn"
            >
              <Share2 className="w-4 h-4 ml-2" />
              مشاركة
            </Button>
            <Button
              onClick={() => {
                const shopName = storeSettings?.shop_name || "حساباتي";
                const periodLine = (filters.date_from || filters.date_to)
                  ? `📅 الفترة: ${filters.date_from || "البداية"} → ${filters.date_to || "اليوم"}\n`
                  : "";
                const openingLine = statement.opening_balance !== 0
                  ? `💼 رصيد افتتاحي: ${formatNumber(statement.opening_balance)} د.ع\n`
                  : "";
                const txLines = (statement.transactions || []).slice(-10).map(tx => {
                  const sign = tx.debit > 0 ? "+" : "-";
                  const amt = tx.debit > 0 ? tx.debit : tx.credit;
                  const pending = tx._offline_pending ? " ⏳" : "";
                  return `• ${tx.date} — ${tx.type}: ${sign}${formatNumber(amt)}${pending}`;
                }).join("\n");
                const balanceLabel = statement.balance < 0
                  ? `عليه (لصالح العميل): ${formatNumber(Math.abs(statement.balance))}`
                  : statement.balance > 0
                    ? `المطلوب منه: ${formatNumber(statement.balance)}`
                    : `الرصيد صفر`;
                const offlineNote = statement._offline ? "\n\n📡 ملاحظة: هذا الكشف حُسب محلياً (وضع أوفلاين)" : "";

                const message =
`🏪 *${shopName}*
📋 *كشف حساب ${filters.type === "customer" ? "عميل" : "مورد"}*
━━━━━━━━━━━━━━━━━
👤 الاسم: ${statement.entity_name}
${statement.entity_phone ? `📞 الهاتف: ${statement.entity_phone}\n` : ""}${periodLine}${openingLine}━━━━━━━━━━━━━━━━━
${txLines || "لا توجد حركات في هذه الفترة"}
━━━━━━━━━━━━━━━━━
🔴 إجمالي المدين: ${formatNumber(statement.total_debit)} د.ع
🟢 إجمالي الدائن: ${formatNumber(statement.total_credit)} د.ع

💰 *${balanceLabel} د.ع*${offlineNote}

🙏 نشكركم على تعاملكم معنا`;

                const phone = (statement.entity_phone || "").replace(/\D/g, "");
                const encoded = encodeURIComponent(message);
                const url = phone
                  ? `https://wa.me/${phone.startsWith("964") ? phone : "964" + phone.replace(/^0/, "")}?text=${encoded}`
                  : `https://wa.me/?text=${encoded}`;
                window.open(url, "_blank");
                toast.success(phone ? "جاري فتح واتساب..." : "اختر جهة الاتصال");
              }}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="whatsapp-statement-btn"
            >
              <MessageCircle className="w-4 h-4 ml-2" />
              واتساب
            </Button>
          </div>

          {/* Printable Statement */}
          <div ref={printRef} className="bg-white rounded-lg overflow-hidden print:rounded-none">
            <div className="p-8 text-black" dir="rtl">
              {/* Header */}
              <div className="flex items-start justify-between border-b-4 border-amber-500 pb-6 mb-6">
                <div className="flex items-center gap-4">
                  {storeSettings?.logo_url ? (
                    <img
                      src={storeSettings.logo_url}
                      alt="شعار المحل"
                      className="w-24 h-24 object-contain"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-amber-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-12 h-12 text-amber-500" />
                    </div>
                  )}
                  <div>
                    <h1 className="text-3xl font-bold text-amber-600">
                      {storeSettings?.store_name || "اسم المحل"}
                    </h1>
                    {storeSettings?.store_name_secondary && (
                      <p className="text-xl text-amber-500">{storeSettings.store_name_secondary}</p>
                    )}
                  </div>
                </div>
                <div className="text-left">
                  <h2 className="text-2xl font-bold text-amber-600">كشف حساب دينار</h2>
                </div>
              </div>

              {/* Customer/Supplier Info */}
              <div className="flex justify-between items-center bg-gray-100 p-4 rounded-lg mb-6">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-amber-500" />
                  <span className="font-bold text-lg">{statement.entity_name}</span>
                </div>
                <div className="text-sm text-gray-600">
                  {filters.date_from && filters.date_to && (
                    <span>الفترة: {formatDate(filters.date_from)} - {formatDate(filters.date_to)}</span>
                  )}
                </div>
              </div>

              {/* Transactions Table */}
              <table className="w-full border-collapse mb-6">
                <thead>
                  <tr className="bg-amber-500 text-white">
                    <th className="border border-amber-600 p-3 text-center w-12">ت</th>
                    <th className="border border-amber-600 p-3 text-center w-28">التاريخ</th>
                    <th className="border border-amber-600 p-3 text-center w-24">نوع الحركة</th>
                    <th className="border border-amber-600 p-3 text-right">البيان</th>
                    <th className="border border-amber-600 p-3 text-center w-32">مدين IQ</th>
                    <th className="border border-amber-600 p-3 text-center w-32">دائن IQ</th>
                    <th className="border border-amber-600 p-3 text-center w-36">الرصيد IQ</th>
                  </tr>
                </thead>
                <tbody>
                  {statement.opening_balance !== 0 && (
                    <tr className="bg-amber-50">
                      <td className="border border-gray-300 p-2 text-center">-</td>
                      <td className="border border-gray-300 p-2 text-center">
                        {filters.date_from ? formatDate(filters.date_from) : "-"}
                      </td>
                      <td className="border border-gray-300 p-2 text-center">-</td>
                      <td className="border border-gray-300 p-2 font-bold">رصيد سابق ماقبل الفترة</td>
                      <td className="border border-gray-300 p-2 text-center">
                        {statement.opening_balance > 0 ? formatNumber(statement.opening_balance) : "-"}
                      </td>
                      <td className="border border-gray-300 p-2 text-center">
                        {statement.opening_balance < 0 ? formatNumber(Math.abs(statement.opening_balance)) : "-"}
                      </td>
                      <td className="border border-gray-300 p-2 text-center font-bold">
                        {formatNumber(statement.opening_balance)}
                      </td>
                    </tr>
                  )}
                  {statement.transactions.map((tx, index) => (
                    <tr key={tx.id} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="border border-gray-300 p-2 text-center">{index + 1}</td>
                      <td className="border border-gray-300 p-2 text-center">{formatDate(tx.date)}</td>
                      <td className="border border-gray-300 p-2 text-center">
                        {tx.type}
                        {tx.exchange_rate && storeSettings?.show_usd && (
                          <span className="text-[9px] text-emerald-600 block mt-0.5" title="سعر الصرف وقت الفاتورة">
                            🔒 1$={Number(tx.exchange_rate).toLocaleString()}
                          </span>
                        )}
                      </td>
                      <td className="border border-gray-300 p-2">{tx.description}</td>
                      <td className="border border-gray-300 p-2 text-center">
                        {tx.debit > 0 ? (
                          <>
                            {formatNumber(tx.debit)}
                            {tx.exchange_rate && storeSettings?.show_usd && (
                              <div className="text-[9px] text-emerald-600" dir="ltr">
                                ≈ ${(tx.debit / tx.exchange_rate).toLocaleString('en-US', {maximumFractionDigits: 2})}
                              </div>
                            )}
                          </>
                        ) : "-"}
                      </td>
                      <td className="border border-gray-300 p-2 text-center">
                        {tx.credit > 0 ? (
                          <>
                            {formatNumber(tx.credit)}
                            {tx.exchange_rate && storeSettings?.show_usd && (
                              <div className="text-[9px] text-emerald-600" dir="ltr">
                                ≈ ${(tx.credit / tx.exchange_rate).toLocaleString('en-US', {maximumFractionDigits: 2})}
                              </div>
                            )}
                          </>
                        ) : "-"}
                      </td>
                      <td className="border border-gray-300 p-2 text-center font-bold">
                        {formatNumber(tx.running_balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="flex justify-end mb-8">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-gray-100 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">إجمالي مدين IQ</p>
                    <p className="text-xl font-bold text-red-600">{formatNumber(statement.total_debit)}</p>
                    {storeSettings?.show_usd && storeSettings?.usd_exchange_rate > 0 && (
                      <p className="text-[11px] text-gray-500 mt-1" dir="ltr">
                        ≈ ${(statement.total_debit / storeSettings.usd_exchange_rate).toLocaleString('en-US', {maximumFractionDigits: 2})}
                      </p>
                    )}
                  </div>
                  <div className="bg-gray-100 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">إجمالي دائن IQ</p>
                    <p className="text-xl font-bold text-green-600">{formatNumber(statement.total_credit)}</p>
                    {storeSettings?.show_usd && storeSettings?.usd_exchange_rate > 0 && (
                      <p className="text-[11px] text-gray-500 mt-1" dir="ltr">
                        ≈ ${(statement.total_credit / storeSettings.usd_exchange_rate).toLocaleString('en-US', {maximumFractionDigits: 2})}
                      </p>
                    )}
                  </div>
                  <div className={`p-4 rounded-lg ${statement.balance < 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                    <p className="text-sm text-gray-600">الرصيد IQ</p>
                    <p className={`text-xl font-bold ${statement.balance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatNumber(statement.balance)}
                      <span className="text-sm mr-1">
                        {statement.balance < 0 ? "(له)" : statement.balance > 0 ? "(عليه)" : ""}
                      </span>
                    </p>
                    {storeSettings?.show_usd && storeSettings?.usd_exchange_rate > 0 && (
                      <p className="text-[11px] text-gray-700 mt-1 font-semibold" dir="ltr">
                        ≈ ${(statement.balance / storeSettings.usd_exchange_rate).toLocaleString('en-US', {maximumFractionDigits: 2})}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t-2 border-amber-500 pt-4 mt-8">
                <div className="flex justify-between items-center text-sm text-gray-600">
                  <div className="flex items-center gap-4">
                    {storeSettings?.phone1 && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4 text-amber-500" />
                        {storeSettings.phone1}
                      </span>
                    )}
                    {storeSettings?.phone2 && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4 text-amber-500" />
                        {storeSettings.phone2}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-amber-500" />
                    <span>{storeSettings?.city} {storeSettings?.address ? `- ${storeSettings.address}` : ""}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center mt-3 text-xs text-gray-500">
                  <span>{storeSettings?.footer_text || "الأخطاء والسهو مرجوع للطرفين"}</span>
                  <span>صفحة 1 من 1</span>
                  <span>{currentDate}</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Empty State */}
      {!statement && !loading && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">
              اختر العميل أو المورد واضغط "عرض كشف الحساب"
            </p>
          </CardContent>
        </Card>
      )}

      {/* ✨ Quick Payment Modal — تسديد سريع */}
      {showPayModal && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => !paySubmitting && setShowPayModal(false)}
          data-testid="quick-payment-modal"
        >
          <div
            className="bg-slate-900 border border-emerald-500/30 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-slate-700 bg-emerald-500/10 flex items-center justify-between">
              <h3 className="text-emerald-300 font-bold flex items-center gap-2">
                💵 تسديد سريع
              </h3>
              <button
                onClick={() => !paySubmitting && setShowPayModal(false)}
                className="text-slate-400 hover:text-white"
                data-testid="quick-payment-close-btn"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-3">
              {statement?.entity_name && (
                <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700 text-sm">
                  <p className="text-slate-400 text-xs">الزبون:</p>
                  <p className="text-white font-bold">{statement.entity_name}</p>
                  {statement.balance > 0 && (
                    <p className="text-amber-400 text-xs mt-1">
                      الرصيد المطلوب منه: {statement.balance.toLocaleString()} د.ع
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label className="text-slate-200 text-sm block mb-1">
                  المبلغ المسدّد (د.ع) *
                </Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  placeholder="0"
                  className="bg-slate-800 border-slate-700 text-white text-lg font-mono"
                  data-testid="quick-payment-amount-input"
                  autoFocus
                />
                {statement?.balance > 0 && (
                  <button
                    type="button"
                    onClick={() => setPayAmount(String(statement.balance))}
                    className="mt-1 text-xs text-emerald-400 hover:underline"
                    data-testid="quick-payment-fill-full-btn"
                  >
                    تسديد كامل ({statement.balance.toLocaleString()} د.ع)
                  </button>
                )}
              </div>

              <div>
                <Label className="text-slate-200 text-sm block mb-1">
                  ملاحظات (اختياري)
                </Label>
                <textarea
                  value={payNotes}
                  onChange={(e) => setPayNotes(e.target.value)}
                  rows={2}
                  placeholder="مثال: تسديد نقدي حضوري"
                  className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 resize-none"
                  data-testid="quick-payment-notes-input"
                />
              </div>
            </div>

            <div className="flex gap-2 p-3 border-t border-slate-700">
              <Button
                variant="outline"
                onClick={() => setShowPayModal(false)}
                disabled={paySubmitting}
                className="flex-1 border-slate-600 text-slate-300"
                data-testid="quick-payment-cancel-btn"
              >
                إلغاء
              </Button>
              <Button
                onClick={submitQuickPayment}
                disabled={paySubmitting || !payAmount || parseFloat(payAmount) <= 0}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="quick-payment-confirm-btn"
              >
                {paySubmitting ? "جاري التسجيل..." : "✅ تأكيد الدفعة"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
